import { ApiService } from '@supply-mover-2-0/core';
import { ToastAPI } from '@supply-mover-2-0/web-ui';

/**
 * Generic batch operation interface
 */
export interface BatchOperation {
	type: 'add' | 'update' | 'remove';
	itemId?: number; // For regular inventory items
	orderDetailId?: number; // For non-inventory items
	description?: string; // For adding new non-inventory items
	quantity?: number;
	price?: number;
}

/**
 * Generic batch context - can be extended per entity type
 */
export interface BatchContext {
	shippingAddressId?: number;
	billingAddressId?: number;
	[key: string]: any; // Allow additional context fields
}

/**
 * Pending batch data structure
 */
export interface PendingBatch<TContext extends BatchContext = BatchContext> {
	entityId: number;
	operations: Map<string, BatchOperation>; // operation key -> operation (latest wins)
	context: TContext; // Context for batch operations
	timeoutId: NodeJS.Timeout | null;
	resolvers: Array<(value: any) => void>; // All promise resolvers
	rejectors: Array<(error: any) => void>; // All promise rejectors
}

/**
 * Configuration for batch queue behavior
 */
export interface BatchQueueConfig<
	TContext extends BatchContext = BatchContext,
> {
	entityName: string; // e.g., 'cart', 'quote' - for error messages and debugging
	batchDelay?: number; // Default: 800ms
	apiEndpoint: (entityId: number) => string; // e.g., (id) => `/orders/cart/${id}/batch`
	processResponse?: (response: any, entityId: number) => void; // Custom response processing
	handleError?: (
		error: any,
		entityId: number,
		operations: BatchOperation[],
	) => void; // Custom error handling
	/**
	 * When true, the batch queue won't surface its own generic error toast.
	 * Use this when the consumer (e.g. useCartOperations) shows a more specific
	 * toast via the rejected promise — otherwise the user sees two toasts at
	 * once. Defaults to false to preserve existing behavior for other consumers.
	 */
	suppressErrorToast?: boolean;
}

/**
 * Generic batch queue class
 */
class BatchQueue<TContext extends BatchContext = BatchContext> {
	private pendingBatches = new Map<number, PendingBatch<TContext>>();
	private readonly config: Required<BatchQueueConfig<TContext>>;

	constructor(config: BatchQueueConfig<TContext>) {
		this.config = {
			batchDelay: 800,
			processResponse: () => {}, // Default no-op
			handleError: (error) => {
				console.error(`[BatchQueue:${config.entityName}] Error:`, error);
			}, // Default error logging
			suppressErrorToast: false,
			...config,
		};
	}

	/**
	 * Queue an operation to be batched
	 */
	async enqueueOperation(
		entityId: number,
		operation: BatchOperation,
		context?: Partial<TContext>,
	): Promise<any> {
		return new Promise((resolve, reject) => {
			let batch = this.pendingBatches.get(entityId);

			if (!batch) {
				// Create new batch
				batch = {
					entityId,
					operations: new Map(),
					context: (context || {}) as TContext,
					timeoutId: null,
					resolvers: [resolve],
					rejectors: [reject],
				};
				this.pendingBatches.set(entityId, batch);
			} else {
				// Clear existing timeout since we're adding to the batch
				if (batch.timeoutId) {
					clearTimeout(batch.timeoutId);
				}

				// Add resolve/reject to the arrays instead of replacing
				batch.resolvers.push(resolve);
				batch.rejectors.push(reject);

				// Update context if provided
				if (context) {
					batch.context = { ...batch.context, ...context };
				}
			}

			// Smart consolidation: merge operations for the same item
			this.consolidateOperation(batch, operation);

			// Set new timeout to process the batch
			batch.timeoutId = setTimeout(() => {
				this.processBatch(entityId);
			}, this.config.batchDelay);
		});
	}

	/**
	 * Generate a unique key for an operation based on itemId or orderDetailId
	 */
	private getOperationKey(operation: BatchOperation): string {
		if (operation.itemId) {
			return `item_${operation.itemId}`;
		} else if (operation.orderDetailId) {
			return `orderDetail_${operation.orderDetailId}`;
		} else if (operation.description && operation.type === 'add') {
			// New non-inventory item - use description + timestamp as key
			return `newItem_${operation.description}_${Date.now()}`;
		} else {
			throw new Error(
				'Operation must have either itemId, orderDetailId, or description (for new items)',
			);
		}
	}

	/**
	 * Create a new operation preserving the correct ID fields
	 */
	private createOperation(
		template: BatchOperation,
		overrides: Partial<BatchOperation>,
	): BatchOperation {
		const newOp: BatchOperation = {
			type: overrides.type ?? template.type,
			quantity: overrides.quantity ?? template.quantity,
			price: overrides.price ?? template.price,
			description: overrides.description ?? template.description,
		};

		// Preserve the correct ID field
		if (template.itemId) {
			newOp.itemId = template.itemId;
		} else if (template.orderDetailId) {
			newOp.orderDetailId = template.orderDetailId;
		}

		return newOp;
	}

	/**
	 * Smart consolidation of operations for the same item
	 */
	private consolidateOperation(
		batch: PendingBatch<TContext>,
		newOperation: BatchOperation,
	) {
		const operationKey = this.getOperationKey(newOperation);
		const existingOperation = batch.operations.get(operationKey);

		if (!existingOperation) {
			// No existing operation, just add the new one
			batch.operations.set(operationKey, newOperation);
			return;
		}

		// Consolidate operations based on type combinations
		if (existingOperation.type === 'add' && newOperation.type === 'add') {
			// Multiple adds: combine quantities
			const totalQuantity =
				(existingOperation.quantity || 1) + (newOperation.quantity || 1);
			const consolidatedOp = this.createOperation(newOperation, {
				type: 'add',
				quantity: totalQuantity,
				price: newOperation.price ?? existingOperation.price, // Use latest price if provided
			});
			batch.operations.set(operationKey, consolidatedOp);
		} else if (
			existingOperation.type === 'add' &&
			newOperation.type === 'update'
		) {
			// Add followed by update: convert to single add with update values
			const consolidatedOp = this.createOperation(newOperation, {
				type: 'add',
				quantity: newOperation.quantity ?? existingOperation.quantity,
				price: newOperation.price ?? existingOperation.price,
			});
			batch.operations.set(operationKey, consolidatedOp);
		} else if (
			existingOperation.type === 'update' &&
			newOperation.type === 'add'
		) {
			// Update followed by add: add to existing quantity
			const totalQuantity =
				(existingOperation.quantity || 0) + (newOperation.quantity || 1);
			const consolidatedOp = this.createOperation(newOperation, {
				type: 'update',
				quantity: totalQuantity,
				price: newOperation.price ?? existingOperation.price,
			});
			batch.operations.set(operationKey, consolidatedOp);
		} else if (
			existingOperation.type === 'update' &&
			newOperation.type === 'update'
		) {
			// Multiple updates: use latest values
			const consolidatedOp = this.createOperation(newOperation, {
				type: 'update',
				quantity: newOperation.quantity ?? existingOperation.quantity,
				price: newOperation.price ?? existingOperation.price,
			});
			batch.operations.set(operationKey, consolidatedOp);
		} else if (newOperation.type === 'remove') {
			// Remove always wins (clears any previous operations)
			batch.operations.set(operationKey, newOperation);
		} else {
			// Default case: latest operation wins (maintains backward compatibility)
			batch.operations.set(operationKey, newOperation);
		}
	}

	/**
	 * Process and send the batch to the server
	 */
	private async processBatch(entityId: number) {
		const batch = this.pendingBatches.get(entityId);
		if (!batch) {
			return;
		}

		// Remove from pending batches
		this.pendingBatches.delete(entityId);

		const operations = Array.from(batch.operations.values());

		try {
			const payload = {
				operations,
				...batch.context,
			};

			// Send batch to API endpoint
			const result = await ApiService.apiPost(
				this.config.apiEndpoint(entityId),
				payload,
			);

			if (result.success) {
				// Custom response processing
				this.config.processResponse(result, entityId);

				// Resolve all pending operations
				batch.resolvers.forEach((resolve) => resolve(result));
			} else {
				// Custom error handling
				const error = new Error(result.error || 'Batch operation failed');
				this.config.handleError(error, entityId, operations);

				// Reject all pending operations
				batch.rejectors.forEach((reject) => reject(error));
			}
		} catch (error) {
			// Custom error handling
			this.config.handleError(error, entityId, operations);

			if (!this.config.suppressErrorToast) {
				// Show user-friendly error
				ToastAPI.show({
					type: 'error',
					text1: `${this.config.entityName} Update Failed`,
					text2: 'Please try again or refresh the page.',
					duration: 4000,
				});
			}

			// Reject all pending operations
			batch.rejectors.forEach((reject) => reject(error));
		}
	}

	/**
	 * Get status for debugging
	 */
	getStatus() {
		return {
			entityName: this.config.entityName,
			pendingBatches: this.pendingBatches.size,
			batchDetails: Array.from(this.pendingBatches.entries()).map(
				([entityId, batch]) => ({
					entityId,
					operationCount: batch.operations.size,
					operations: Array.from(batch.operations.values()),
				}),
			),
		};
	}

	/**
	 * Force flush all pending batches immediately
	 */
	async flushAll() {
		const promises = Array.from(this.pendingBatches.keys()).map((entityId) => {
			const batch = this.pendingBatches.get(entityId);
			if (batch?.timeoutId) {
				clearTimeout(batch.timeoutId);
			}
			return this.processBatch(entityId);
		});

		await Promise.all(promises);
	}

	/**
	 * Force flush pending batch for a specific entity immediately
	 */
	async flushEntity(entityId: number) {
		const batch = this.pendingBatches.get(entityId);
		if (batch) {
			if (batch.timeoutId) {
				clearTimeout(batch.timeoutId);
			}
			await this.processBatch(entityId);
		}
	}
}

/**
 * Factory function to create batch queue instances
 */
export function createBatchQueue<TContext extends BatchContext = BatchContext>(
	config: BatchQueueConfig<TContext>,
): BatchQueue<TContext> {
	return new BatchQueue<TContext>(config);
}

// Export the class for consumers (the interfaces above are exported at declaration)
export { BatchQueue };
